import { Box } from "@mui/material";
import { Fragment, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { WageBreakdownResult } from "../../utils/models";
import { formatCents, formatClockDuration, formatShiftDate, formatTimeRange, MINUTES_PER_HOUR } from "./consts";
import { SCOPE_SEPARATOR } from "./scopeText";
import type { WageComposition } from "./wageComposition";

/** The figures a reader scans for — the shift's length and its base — stand out from the words around them. */
const FIGURE = { b: <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }} /> };

type Part = { key: string; node: ReactNode };

/**
 * The shift's constants as one line: its date, its paid length (unpaid breaks out, as the row shows it) —
 * scheduled, or punched with the punch times —
 * the base it shares, and weekend. The base is left out while wages are hidden: it is an amount, and
 * "base -/h" says nothing. Empty when there is nothing to state.
 */
export const useHeaderConstants = (breakdown: WageBreakdownResult, composition: WageComposition, paidHours: number, canViewWage: boolean): ReactNode[] => {
	const { t } = useTranslation();
	const { shiftStartTime, shiftEndTime, isActual } = breakdown;
	const whenPart: Part[] = [];
	if (shiftStartTime && shiftEndTime) {
		const hours = formatClockDuration(paidHours * MINUTES_PER_HOUR);
		whenPart.push(
			{ key: "date", node: formatShiftDate(shiftStartTime) },
			{
				key: "length",
				node: isActual ? (
					<Trans i18nKey="wageBreakdown.header.punched" values={{ hours }} components={FIGURE} />
				) : (
					<Trans i18nKey="wageBreakdown.header.scheduled" values={{ hours }} components={FIGURE} />
				),
			},
			...(isActual ? [{ key: "range", node: formatTimeRange(shiftStartTime, shiftEndTime) }] : []),
		);
	}
	const basePart: Part[] =
		canViewWage && composition.sharedBaseCents !== undefined
			? [
					{
						key: "base",
						node: <Trans i18nKey="wageBreakdown.header.base" values={{ amount: formatCents(composition.sharedBaseCents) }} components={FIGURE} />,
					},
				]
			: [];
	const weekendPart = composition.hasWeekendHours ? [{ key: "weekend", node: t("wageBreakdown.header.weekend") }] : [];
	return [...whenPart, ...basePart, ...weekendPart].map(({ key, node }, index) => (
		<Fragment key={key}>
			{index > 0 && SCOPE_SEPARATOR}
			{node}
		</Fragment>
	));
};
